import { PLAYER } from "@tiny-seas/shared";

/**
 * One locomotion gait, measured in travelled metres so the legs, footstep audio and
 * first-person head bob share a single cadence at any frame rate.
 */
export interface Gait {
  /** Metres between consecutive footfalls (alternating feet). */
  readonly step: number;
  /** A planted foot passes under the hips from +reach to -reach; bounded by the rig's leg reach. */
  readonly reach: number;
  /** Peak swing-foot lift (m). */
  readonly lift: number;
  /** Hip rise from mid-stance to mid-flight (m). */
  readonly bounce: number;
  /** Fore-aft travel (m) of a free hand swinging with the opposite leg; the sprint pump's drive. */
  readonly armSwing: number;
  /** Fore-aft sway (m) of a hand carrying a tool, in time with the same leg cadence. */
  readonly carrySway: number;
}

// `step` is the shared speed times seconds per footfall: 3.6 (walk), 4 (sprint),
// 3.3 (carry) and 3.8 (crouch) steps a second. Each foot is planted for
// reach / step of its cycle; the rest is swing and, below one half, flight.
const WALK: Gait = { step: PLAYER.walkSpeed * 0.28, reach: 0.22, lift: 0.16, bounce: 0.05, armSwing: 0.13, carrySway: 0.03 };
// Sprinting hands, tools included, pump with `armSwing` instead of swaying.
const SPRINT: Gait = { step: PLAYER.sprintSpeed * 0.25, reach: 0.30, lift: 0.24, bounce: 0.06, armSwing: 0.12, carrySway: 0 };
// Carrying is the chest: both hands are on it.
const CARRY: Gait = { step: PLAYER.carrySpeed * 0.30, reach: 0.22, lift: 0.10, bounce: 0.025, armSwing: 0, carrySway: 0 };
const CROUCH: Gait = { step: PLAYER.crouchSpeed * 0.26, reach: 0.24, lift: 0.06, bounce: 0.01, armSwing: 0.06, carrySway: 0.02 };

/** Crouching outranks sprinting, which outranks carrying, matching the movement rules. */
export function gaitFor(crouched: boolean, sprinting: boolean, carrying: boolean): Gait {
  return crouched ? CROUCH : sprinting ? SPRINT : carrying ? CARRY : WALK;
}
